<template>
  <div class="p-4 md:p-6 bg-slate-50 dark:bg-gray-900 min-h-screen font-sans">
    <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-slate-100 dark:border-gray-700 p-4 md:p-6">
      <div class="flex justify-between items-center mb-6">
        <div class="flex items-center space-x-3">
          <div class="h-10 w-1.5 bg-gradient-to-b from-indigo-500 to-purple-600 rounded-full"></div>
          <div>
            <h1 class="text-xl md:text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
              <Icon icon="mdi:file-document-outline" class="text-indigo-600" />
              Detalle Cotización
            </h1>
          </div>
        </div>
        
        <router-link to="/cotizaciones" class="text-slate-500 hover:text-slate-700 dark:text-gray-400 dark:hover:text-white">
          <Icon icon="mdi:arrow-left" class="text-lg" />
          Volver
        </router-link>
      </div>

      <div v-if="cargando" class="flex justify-center py-12">
        <Icon icon="mdi:loading" class="text-4xl text-indigo-500 animate-spin" />
      </div>

      <div v-else-if="cotizacion" class="space-y-6">
        <!-- Datos de la cotización -->
        <div class="bg-slate-50 dark:bg-gray-700/30 rounded-xl p-4 border border-slate-200 dark:border-gray-600">
          <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p class="text-[10px] font-black text-slate-400 uppercase">N° Cotización</p>
              <p class="text-sm font-bold text-slate-800 dark:text-white">{{ cotizacion.numeroCotizacion }}</p>
            </div>
            <div>
              <p class="text-[10px] font-black text-slate-400 uppercase">Fecha Emisión</p>
              <p class="text-sm font-bold text-slate-800 dark:text-white">{{ formatDate(cotizacion.fechaEmision) }}</p>
            </div>
            <div>
              <p class="text-[10px] font-black text-slate-400 uppercase">Fecha Validez</p>
              <p class="text-sm font-bold text-slate-800 dark:text-white">{{ formatDate(cotizacion.fechaValidez) }}</p>
            </div>
            <div>
              <p class="text-[10px] font-black text-slate-400 uppercase">Estado</p>
              <span :class="getEstadoClass(cotizacion.estado)" class="px-3 py-1 rounded-full text-[10px] font-bold uppercase inline-block">
                {{ getEstadoLabel(cotizacion.estado) }}
              </span>
            </div>
          </div>
        </div>

        <!-- Cliente -->
        <div class="bg-slate-50 dark:bg-gray-700/30 rounded-xl p-4 border border-slate-200 dark:border-gray-600">
          <h3 class="text-xs font-black text-slate-400 uppercase mb-2">Cliente</h3>
          <div class="grid grid-cols-2 gap-4">
            <div>
              <p class="text-sm font-bold text-slate-800 dark:text-white">{{ cotizacion.clienteNombre || 'N/A' }}</p>
              <p class="text-xs text-slate-500">Documento: {{ cotizacion.clienteDocumento || 'N/A' }}</p>
            </div>
          </div>
        </div>

        <!-- Detalles -->
        <div class="bg-slate-50 dark:bg-gray-700/30 rounded-xl p-4 border border-slate-200 dark:border-gray-600">
          <h3 class="text-xs font-black text-slate-400 uppercase mb-2">Items</h3>
          <div class="overflow-x-auto">
            <table class="w-full text-left text-sm">
              <thead>
                <tr class="border-b border-slate-200 dark:border-gray-600">
                  <th class="py-2 text-[10px] font-black text-slate-400 uppercase">Descripción</th>
                  <th class="py-2 text-[10px] font-black text-slate-400 uppercase text-center">Cant.</th>
                  <th class="py-2 text-[10px] font-black text-slate-400 uppercase text-right">Precio USD</th>
                  <th class="py-2 text-[10px] font-black text-slate-400 uppercase text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in cotizacion.detalles" :key="item.idCotizacionDetalle" class="border-b border-slate-100 dark:border-gray-700/50">
                  <td class="py-2 text-slate-700 dark:text-slate-300">
                    {{ item.descripcionManual || item.productoNombre || item.servicioNombre || 'Sin descripción' }}
                  </td>
                  <td class="py-2 text-slate-700 dark:text-slate-300 text-center">{{ item.cantidad }}</td>
                  <td class="py-2 text-slate-700 dark:text-slate-300 text-right">${{ item.precioUnitarioUsd?.toFixed(2) }}</td>
                  <td class="py-2 font-bold text-indigo-600 dark:text-indigo-400 text-right">${{ item.totalUsd?.toFixed(2) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Totales -->
        <div class="bg-slate-50 dark:bg-gray-700/30 rounded-xl p-4 border border-slate-200 dark:border-gray-600">
          <div class="flex flex-col items-end space-y-1">
            <div class="flex justify-between w-64">
              <span class="text-sm text-slate-500">Subtotal:</span>
              <span class="text-sm font-bold text-slate-700 dark:text-white">${{ cotizacion.subtotalUsd?.toFixed(2) }}</span>
            </div>
            <div class="flex justify-between w-64" v-if="cotizacion.descuentoUsd > 0">
              <span class="text-sm text-slate-500">Descuento:</span>
              <span class="text-sm font-bold text-rose-500">-${{ cotizacion.descuentoUsd?.toFixed(2) }}</span>
            </div>
            <div class="flex justify-between w-64">
              <span class="text-sm text-slate-500">IVA ({{ cotizacion.ivaPorcentaje || 16 }}%):</span>
              <span class="text-sm font-bold text-slate-700 dark:text-white">${{ cotizacion.ivaUsd?.toFixed(2) }}</span>
            </div>
            <div class="flex justify-between w-64 pt-2 border-t border-slate-200 dark:border-gray-600">
              <span class="text-lg font-bold text-slate-700 dark:text-white">Total:</span>
              <span class="text-lg font-bold text-emerald-600 dark:text-emerald-400">${{ cotizacion.totalUsd?.toFixed(2) }}</span>
            </div>
          </div>
        </div>

        <!-- Observaciones -->
        <div v-if="cotizacion.observaciones" class="bg-slate-50 dark:bg-gray-700/30 rounded-xl p-4 border border-slate-200 dark:border-gray-600">
          <h3 class="text-xs font-black text-slate-400 uppercase mb-2">Observaciones</h3>
          <p class="text-sm text-slate-600 dark:text-slate-300">{{ cotizacion.observaciones }}</p>
        </div>

        <!-- Botones de acción -->
        <div class="flex flex-wrap gap-3">
          <button v-if="cotizacion.estado === 'borrador'" @click="enviarCotizacion" class="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 rounded-xl font-bold transition-all flex items-center gap-2 text-sm">
            <Icon icon="mdi:send" /> Enviar
          </button>
          <button v-if="cotizacion.estado === 'enviada'" @click="aprobarCotizacion" class="bg-emerald-600 hover:bg-emerald-700 text-white px-5 py-2.5 rounded-xl font-bold transition-all flex items-center gap-2 text-sm">
            <Icon icon="mdi:check-circle" /> Aprobar
          </button>
          <button v-if="cotizacion.estado === 'enviada'" @click="rechazarCotizacion" class="bg-rose-600 hover:bg-rose-700 text-white px-5 py-2.5 rounded-xl font-bold transition-all flex items-center gap-2 text-sm">
            <Icon icon="mdi:close-circle" /> Rechazar
          </button>
          <button v-if="cotizacion.estado === 'aprobada'" @click="convertirAVenta" class="bg-purple-600 hover:bg-purple-700 text-white px-5 py-2.5 rounded-xl font-bold transition-all flex items-center gap-2 text-sm">
            <Icon icon="mdi:cart" /> Convertir a Venta
          </button>
        </div>
      </div>

      <div v-else-if="!cargando" class="text-center py-12">
        <Icon icon="mdi:file-document-outline" class="text-5xl text-slate-300 dark:text-gray-600 mx-auto mb-4" />
        <p class="text-slate-500 dark:text-gray-400">Cotización no encontrada</p>
        <router-link to="/cotizaciones" class="mt-4 inline-block text-indigo-600 hover:text-indigo-700 font-bold">
          Volver a cotizaciones
        </router-link>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch } from 'vue';
import { Icon } from '@iconify/vue';
import { useRoute, useRouter } from 'vue-router';
import Swal from 'sweetalert2';
import {
  obtenerCotizacionPorId,
  enviarCotizacion as apiEnviar,
  aprobarCotizacion as apiAprobar,
  rechazarCotizacion as apiRechazar,
  convertirCotizacionAVenta
} from '@/api/cotizacion';

const route = useRoute();
const router = useRouter();
const cotizacion = ref(null);
const cargando = ref(true);
const errorCarga = ref(false);

const cargarDatos = async () => {
  // Obtener el ID de la ruta
  const id = route.params.id;
  
  // Verificar si el ID es válido
  if (!id || id === 'undefined' || isNaN(parseInt(id))) {
    console.error('❌ ID de cotización inválido:', id);
    cargando.value = false;
    errorCarga.value = true;
    return;
  }

  cargando.value = true;
  errorCarga.value = false;
  
  try {
    const response = await obtenerCotizacionPorId(parseInt(id));
    if (response.success) {
      cotizacion.value = response.data;
      if (!response.data) {
        errorCarga.value = true;
        toast('Cotización no encontrada', 'error');
      }
    } else {
      errorCarga.value = true;
      toast(response.error || 'Error al cargar cotización', 'error');
    }
  } catch (error) {
    console.error('Error:', error);
    errorCarga.value = true;
    toast('Error al cargar cotización', 'error');
  } finally {
    cargando.value = false;
  }
};

const enviarCotizacion = async () => {
  try {
    const response = await apiEnviar(cotizacion.value.idCotizacion);
    if (response.success) {
      toast('Cotización enviada correctamente');
      await cargarDatos();
    } else {
      toast(response.error || 'Error al enviar', 'error');
    }
  } catch (error) {
    toast('Error al enviar', 'error');
  }
};

const aprobarCotizacion = async () => {
  try {
    const response = await apiAprobar(cotizacion.value.idCotizacion);
    if (response.success) {
      toast('Cotización aprobada correctamente');
      await cargarDatos();
    } else {
      toast(response.error || 'Error al aprobar', 'error');
    }
  } catch (error) {
    toast('Error al aprobar', 'error');
  }
};

const rechazarCotizacion = async () => {
  const { value: motivo } = await Swal.fire({
    title: 'Motivo del rechazo',
    input: 'textarea',
    inputPlaceholder: 'Escribe el motivo del rechazo...',
    showCancelButton: true,
    confirmButtonText: 'Rechazar',
    cancelButtonText: 'Cancelar'
  });

  if (motivo !== undefined) {
    try {
      const response = await apiRechazar(cotizacion.value.idCotizacion, motivo || 'Sin motivo');
      if (response.success) {
        toast('Cotización rechazada correctamente');
        await cargarDatos();
      } else {
        toast(response.error || 'Error al rechazar', 'error');
      }
    } catch (error) {
      toast('Error al rechazar', 'error');
    }
  }
};

const convertirAVenta = async () => {
  try {
    const response = await convertirCotizacionAVenta(cotizacion.value.idCotizacion);
    if (response.success) {
      toast('Cotización convertida a venta correctamente');
      await cargarDatos();
    } else {
      toast(response.error || 'Error al convertir', 'error');
    }
  } catch (error) {
    toast('Error al convertir', 'error');
  }
};

const getEstadoLabel = (estado) => {
  const labels = {
    borrador: 'Borrador',
    enviada: 'Enviada',
    aprobada: 'Aprobada',
    rechazada: 'Rechazada',
    expirada: 'Expirada',
    convertida: 'Convertida'
  };
  return labels[estado] || estado;
};

const getEstadoClass = (estado) => {
  const classes = {
    borrador: 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300',
    enviada: 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400',
    aprobada: 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400',
    rechazada: 'bg-rose-100 text-rose-600 dark:bg-rose-900/30 dark:text-rose-400',
    expirada: 'bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400',
    convertida: 'bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400'
  };
  return classes[estado] || 'bg-slate-100 text-slate-600';
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleDateString('es-VE', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const toast = (message, icon = 'success') => {
  Swal.fire({
    toast: true,
    position: 'top-end',
    showConfirmButton: false,
    timer: 3000,
    timerProgressBar: true,
    icon,
    title: message,
    customClass: { popup: 'rounded-2xl' }
  });
};

// Cargar datos al montar el componente
onMounted(() => {
  cargarDatos();
});

// Si el parámetro cambia, recargar
watch(() => route.params.id, () => {
  cargarDatos();
});
</script>